// ./src/components/features/Account/UserInvitations/UserInvitationFormModal/UserInvitationFormModal.tsx
import React, { useState, useEffect } from "react";
import { TextInput, Stack, Text, Alert } from "@mantine/core";

import { UserInvitation, UserInvitationInput } from "@/utils/api";
import { AppDrawer, AppIcon } from "@/components/generic";
import { USER_ROLES } from "@/config/userRoles";
import { UserRoleSelector } from "../../UserRoleSelector";

export interface UserInvitationEditModalProps {
  /** Whether the modal is open */
  opened: boolean;

  /** Function to close the modal */
  onClose: () => void;

  /** User invitation to edit (if null/undefined, it's create mode) */
  userInvitation?: UserInvitation | null;

  /** Callback when user invitation should be created */
  onCreateUserInvitation?: (
    data: UserInvitationInput
  ) => Promise<UserInvitation | null>;

  /** Callback when user invitation should be updated */
  onUpdateUserInvitation?: (
    id: string,
    data: UserInvitationInput
  ) => Promise<UserInvitation | null>;

  /** Whether create operation is in progress */
  isCreating?: boolean;

  /** Whether update operation is in progress */
  isUpdating?: boolean;

  /** Last error from operations */
  lastError?: string | null;

  /** Callback to clear errors */
  onClearError?: () => void;
}

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  userRoleId: string | null;
}

const initialFormData: FormData = {
  firstName: "",
  lastName: "",
  email: "",
  userRoleId: USER_ROLES.ADMIN,
};

export const UserInvitationEditModal: React.FC<
  UserInvitationEditModalProps
> = ({
  opened,
  onClose,
  userInvitation,
  onCreateUserInvitation,
  onUpdateUserInvitation,
  isCreating = false,
  isUpdating = false,
  lastError,
  onClearError,
}) => {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string | null | undefined>
  >({});

  const isEditMode = Boolean(userInvitation?.id);
  const isSubmitting = isCreating || isUpdating;

  // Reset form when modal opens/closes or userInvitation changes
  useEffect(() => {
    if (opened) {
      if (userInvitation) {
        // Edit mode - populate form with data
        setFormData({
          firstName: userInvitation.firstName || "",
          lastName: userInvitation.lastName || "",
          email: userInvitation.email || "",
          userRoleId: userInvitation.userRoleId || USER_ROLES.ADMIN,
        });
      } else {
        // Create mode - use defaults
        setFormData(initialFormData);
      }

      setValidationErrors({});
      onClearError?.();
    }
  }, [opened, userInvitation, onClearError]);

  // Validate email format
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validate form
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // First name is required
    if (!formData.firstName.trim()) {
      errors.firstName = "First name is required";
    }

    // Last name is required
    if (!formData.lastName.trim()) {
      errors.lastName = "Last name is required";
    }

    // Email is required and must be valid
    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!validateEmail(formData.email.trim())) {
      errors.email = "Please enter a valid email address";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle field changes
  const handleFieldChange = (field: keyof FormData, value: string | null) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  // Handle save
  const handleSubmit = async () => {
    onClearError?.();

    if (!validateForm()) {
      return;
    }

    try {
      const submitData: UserInvitationInput = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim().toLowerCase(),
        userRoleId: formData.userRoleId,
      };

      let result: UserInvitation | null = null;

      if (isEditMode && userInvitation?.id && onUpdateUserInvitation) {
        result = await onUpdateUserInvitation(userInvitation.id, submitData);
      } else if (!isEditMode && onCreateUserInvitation) {
        result = await onCreateUserInvitation(submitData);
      }

      if (result) {
        onClose();
      }
    } catch (error) {
      console.error("User invitation submission error:", error);
    }
  };

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={isEditMode ? "Edit User Invitation" : "Invite New User"}
      position="right"
      size="md"
      closable={!isSubmitting}
      loading={isSubmitting}
      errors={lastError}
      actionType="save-cancel"
      onConfirm={handleSubmit}
      //confirmLabel={isEditMode ? "Update" : "Send Invitation"}
      //confirmIconName={isEditMode ? "Check" : "Send"}
    >
      <Stack gap="md">
        <TextInput
          label="First Name"
          placeholder="Enter first name"
          value={formData.firstName}
          onChange={(e) => handleFieldChange("firstName", e.target.value)}
          error={validationErrors.firstName}
          required
          withAsterisk
          size="md"
          description="The invitee's first name"
        />

        <TextInput
          label="Last Name"
          placeholder="Enter last name"
          value={formData.lastName}
          onChange={(e) => handleFieldChange("lastName", e.target.value)}
          error={validationErrors.lastName}
          required
          withAsterisk
          size="md"
          description="The invitee's last name"
        />

        <TextInput
          label="Email"
          placeholder="Enter email address"
          value={formData.email}
          onChange={(e) => handleFieldChange("email", e.target.value)}
          error={validationErrors.email}
          required
          withAsterisk
          size="md"
          type="email"
          description="The invitation will be sent to this email address"
        />

        <UserRoleSelector
          label="User Role"
          placeholder="Select user role"
          value={formData.userRoleId}
          onChange={(value) => handleFieldChange("userRoleId", value)}
          error={validationErrors.userRoleId}
          required
        />

        {!isEditMode && (
          <Alert
            color="blue"
            icon={<AppIcon iconName="InfoCircle" size={16} />}
          >
            <Text size="sm">
              An invitation email will be sent to the provided email address.
              The invitation will be valid for 7 days.
            </Text>
          </Alert>
        )}
      </Stack>
    </AppDrawer>
  );
};
